/**
 * Editing an inline-loop chip in place, the counterpart of conditionalRunEdit.ts: clicking one (or Enter while it is
 * selected) asks the page to open LoopRunPopover for it, and applyLoopRunEdit writes the result back as one
 * transaction with its own history step.
 *
 * The collection keeps any loop formatters (`items | sort:price`, `cars | distinct:brand`); its normalized key is the
 * path alone, as LoopSource.NormalizedKey does. The item alias is a plain name (`item` when left empty). The repeated
 * text is edited as template text exactly like a condition branch, and kept verbatim when unchanged; one holding
 * content text cannot express (a nested condition or loop, a link) stays as it is.
 */
import { Extension, type Editor } from '@tiptap/core'
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state'
import { closeHistory } from '@tiptap/pm/history'
import { normalizeMonikerKey } from './monikerKey'
import { placeholderNameError } from './placeholderEdit'
import { branchFormat, branchTextError, branchToText, splitMoniker, textToBranch } from './conditionalRunEdit'
import type { LoopRun } from './types'

type EditRequest = (pos: number) => void
const editRequests = new WeakMap<Editor, EditRequest>()

export function setLoopRunEditHandler(editor: Editor, handler: EditRequest | null) {
  if (handler) editRequests.set(editor, handler)
  else editRequests.delete(editor)
}

export const DEFAULT_ITEM_ALIAS = 'item'

/** Why the collection source cannot be used, or null. Loop formatters after `|` are allowed. */
export function loopSourceError(rawName: string): string | null {
  const t = rawName.trim()
  if (!t) return 'Enter the collection to repeat over.'
  if (/[{}]/.test(t)) return 'The collection cannot contain braces.'
  return placeholderNameError(splitMoniker(t).key)
}

/** Why the alias cannot be used, or null. Empty means the default `item`. */
export function itemAliasError(alias: string): string | null {
  const t = alias.trim()
  if (!t) return null
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(t) ? null : 'An item name is letters, digits and _ and starts with a letter.'
}

/** The collection's normalized key: the path without loop formatters (LoopSource.NormalizedKey). */
export function loopNormalizedKey(rawName: string): string {
  return normalizeMonikerKey(splitMoniker(rawName.trim()).key)
}

export interface LoopRunEdit {
  rawName: string
  itemAlias: string
  /** The repeated text; null keeps it as it is (text cannot express it). */
  text: string | null
}

export function editedLoopRun(run: LoopRun, edit: LoopRunEdit): LoopRun {
  const rawName = edit.rawName.trim()
  const inlines = edit.text === null || branchToText(run.inlines) === edit.text
    ? run.inlines
    : textToBranch(edit.text, branchFormat(run.inlines))
  return {
    ...run,
    rawName,
    normalizedKey: loopNormalizedKey(rawName),
    itemAlias: edit.itemAlias.trim() || DEFAULT_ITEM_ALIAS,
    inlines,
  }
}

export function loopRunEditError(edit: LoopRunEdit): string | null {
  return loopSourceError(edit.rawName) ?? itemAliasError(edit.itemAlias) ?? (edit.text !== null ? branchTextError(edit.text) : null)
}

export function applyLoopRunEdit(editor: Editor, pos: number, edit: LoopRunEdit): boolean {
  const { state, view } = editor
  const node = state.doc.nodeAt(pos)
  if (!node || node.type.name !== 'loopRun' || loopRunEditError(edit)) return false
  const run = node.attrs.json as LoopRun
  const next = editedLoopRun(run, edit)
  if (JSON.stringify(next) === JSON.stringify(run)) return true
  const tr = closeHistory(state.tr)
  tr.setNodeMarkup(pos, undefined, { ...node.attrs, json: next })
  tr.setSelection(NodeSelection.create(tr.doc, pos))
  view.dispatch(tr)
  return true
}

export const LoopRunEditing = Extension.create({
  name: 'loopRunEditing',
  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('loopRunEditing'),
        props: {
          handleClickOn(_view, _pos, node, nodePos, event) {
            if (node.type.name !== 'loopRun' || !editor.isEditable || event.button !== 0) return false
            const request = editRequests.get(editor)
            if (request) window.setTimeout(() => request(nodePos))
            return false
          },
          handleKeyDown(view, event) {
            if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return false
            const sel = view.state.selection
            if (!(sel instanceof NodeSelection) || sel.node.type.name !== 'loopRun') return false
            const request = editRequests.get(editor)
            if (!request || !editor.isEditable) return false
            request(sel.from)
            return true
          },
        },
      }),
    ]
  },
})
